import { AUTHORIZATION_HEADER_NAME, getAuthToken } from 'src/utils/auth';
import { safeApiLog } from '../utils/safeLogging';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '/';


let controller: AbortController;
let signal: AbortSignal;

function _resetAbortController() {
    controller = new AbortController();
    signal = controller.signal;
}

// init controller and signal
_resetAbortController();

export function abortPreviousRequests(): void {
    controller.abort();

    // replace controller and signal so we have a new unaborted one (otherwise the
    // controller remains aborted and will abort any fetch using its signal immediately)
    _resetAbortController();
}

export function login(email: string, password: string): Promise<any> {
    return _callAPI('user/login', 'POST', { email, password });
}

export function signUp(
    first_name: string,
    last_name: string,
    email: string,
    password: string,
    tier_id?: number,
    terms_consent?: {
        terms_version: string;
        consent_timestamp: number;
        browser_user_agent?: string;
        browser_language?: string;
        browser_platform?: string;
        consent_data?: Record<string, any>;
    }
): Promise<any> {
    const payload: any = { 
        first_name, 
        last_name, 
        email, 
        password, 
        tier_id: tier_id || 1 
    };
    
    // Add terms consent data if provided
    if (terms_consent) {
        Object.assign(payload, terms_consent);
    }
    
    return _callAPI('user/sign-up', 'POST', payload);
}

export function getTiers(): Promise<any> {
    return _callAPI('user/tiers', 'GET');
}

export function changePassword(
    email: string,
    old_password: string,
    new_password: string
): Promise<any> {
    return _callAuthenticatedAPI('user/login/change-password', 'POST', {
        email,
        old_password,
        new_password
    });
}

export function verifyToken(token: string): Promise<any> {
    return _callAuthenticatedAPI('user/reset/verify', 'POST', {
        token
    });
}

export function resetPassword(token: string, password: string): Promise<any> {
    return _callAuthenticatedAPI('user/reset/confirm', 'POST', {
        token,
        password
    });
}

export function forgetPassword(email: string): Promise<any> {
    return _callAuthenticatedAPI('user/reset/request', 'POST', {
        email
    });
}

export function getUser(
    type: string,
    page: number,
    limit: number,
    search?: string
): Promise<any> {
    return _callAuthenticatedAPI(
        `user?type=${type}&page=${page}&limit=${limit}` +
            (search ? `&search=${search}` : '')
    );
}


// base URL should all be lower case connected with hypen "-", no camelCase, no Snake Case
// however, camelCase ("thisParameter") or Snake Case ("this_par") can be used for the key parameter names in the query part
export function getDocuments(numDocs?: number): Promise<any> {
    return _callAPI(
        `convert/get-documents/` + (numDocs ? `?numDocs=${numDocs}` : '')
    );
}

export function getMedicalDeviceKDocuments(numDocs?: number, forceReprocess?: boolean, category?: string, subCategory?: string, offset?: number, company?: string): Promise<any> {
  let queryParams = '';
  if (numDocs) {
    queryParams = `?numDocs=${numDocs}`;
  }
  if (forceReprocess !== undefined) {
    queryParams += queryParams ? `&forceReprocess=${forceReprocess}` : `?forceReprocess=${forceReprocess}`;
  }
  if (category) {
    queryParams += queryParams ? `&category=${category}` : `?category=${category}`;
  }
  if (subCategory) {
    queryParams += queryParams ? `&subCategory=${subCategory}` : `?subCategory=${subCategory}`;
  }
  if (offset !== undefined) {
    queryParams += queryParams ? `&offset=${offset}` : `?offset=${offset}`;
  }
  if (company) {
    queryParams += queryParams ? `&company=${encodeURIComponent(company)}` : `?company=${encodeURIComponent(company)}`;
  }
  return _callAPI(`convert/get-medical-device-k-documents/${queryParams}`);
}

export function getMedicalDeviceByKNumber(kNumbers: string[], forceReprocess?: boolean): Promise<any> {
  let queryParams = `?kNumbers=${kNumbers.join(',')}`;
  if (forceReprocess !== undefined) {
    queryParams += `&forceReprocess=${forceReprocess}`;
  }
  return _callAPI(`convert/get-medical-device-by-k-number/${queryParams}`);
}

export function getMedicalDeviceStats(category?: string, subCategory?: string): Promise<any> {
  let queryParams = '';
  if (category) {
    queryParams = `?category=${category}`;
  }
  if (subCategory) {
    queryParams += queryParams ? `&subCategory=${subCategory}` : `?subCategory=${subCategory}`;
  }
  return _callAPI(`convert/get-medical-device-stats/${queryParams}`);
}

export function getPresignedURL(filename: string): Promise<any> {
    return _callAuthenticatedAPI(`upload/presign-upload?filename=${encodeURIComponent(filename)}`, 'GET');
}

export function submitJob(body: { s3_key: string; uuid: string }): Promise<any> {
    return _callAuthenticatedAPI('upload/submit-job', 'POST', body);
}

export function getJobStatus(job_id: string): Promise<any> {
    return _callAuthenticatedAPI(`upload/job_status/${job_id}/`, 'GET');
}

export function adminForceReprocessMedicalDevice(kNumbers: string[]): Promise<any> {
    return _callAuthenticatedAPI('user/admin/force-reprocess-medical-device', 'POST', {
        k_numbers: kNumbers
    });
}

export function getGenes(numGenes?: number, geneIds?: string[], forceUpdate?: boolean): Promise<any> {
    let queryParams = '';
    if (numGenes) {
        queryParams += `?numGenes=${numGenes}`;
    }
    if (geneIds && geneIds.length > 0) {
        const geneIdParams = geneIds.map(id => `geneIds=${encodeURIComponent(id)}`).join('&');
        queryParams += queryParams ? `&${geneIdParams}` : `?${geneIdParams}`;
    }
    if (forceUpdate !== undefined) {
        queryParams += queryParams ? `&forceUpdate=${forceUpdate}` : `?forceUpdate=${forceUpdate}`;
    }
    return _callAPI(`convert/get-genes/${queryParams}`);
}

export function getPDBURL(pdbId: string): Promise<any> {
    return _callAPI(`convert/pdb-url/${pdbId}/`);
}

export function getPresignedPaperUploadURL(filename: string): Promise<any> {
    return _callAuthenticatedAPI(`user/paper/presign-upload?filename=${encodeURIComponent(filename)}`, 'GET');
}

export function processPaperUpload(body: { s3_pdf_path: string; document_uuid: string; user_provided_url?: string }): Promise<any> {
    return _callAuthenticatedAPI('user/paper/process-upload', 'POST', body);
}

export function processPaperURL(body: { paper_url: string }): Promise<any> {
    return _callAuthenticatedAPI('user/paper/process-url', 'POST', body);
}

// MFA (Multi-Factor Authentication) API functions
export function mfaSetup(): Promise<any> {
    return _callAuthenticatedAPI('user/mfa/setup', 'POST', {});
}

export function mfaVerifySetup(code: string): Promise<any> {
    console.log('🔐 API: mfaVerifySetup called with code:', code ? `${code.substring(0, 2)}****` : 'empty');
    return _callAuthenticatedAPI('user/mfa/verify-setup', 'POST', { code });
}

export function mfaVerifyLogin(email: string, code: string): Promise<any> {
    return _callAPI('user/mfa/verify-login', 'POST', { email, code });
}

// Admin APIs (allowed admin only)
export function getAdminMe(): Promise<any> {
    return _callAuthenticatedAPI('user/admin/me', 'GET');
}

export function getAdminUsersList(): Promise<any> {
    return _callAuthenticatedAPI('user/admin/users-list', 'GET');
}

export function getAdminHealth(): Promise<any> {
    return _callAuthenticatedAPI('user/admin/health', 'GET');
}

export function visitPing(): Promise<any> {
    return _callAPI('user/admin/visit-ping', 'POST');
}



type MethodType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'OPTIONS' | 'HEAD';

function _callAuthenticatedAPI(
    endpoint: string,
    method: MethodType = 'GET',
    body?: { [key: string]: any },
    abortable: boolean = false
): Promise<any> {
    const token = getAuthToken();
    console.log('🔍 DEBUG: getAuthToken() returned:', token);
    console.log('🔍 DEBUG: AUTHORIZATION_HEADER_NAME:', AUTHORIZATION_HEADER_NAME);
    
    const headers = {
        [AUTHORIZATION_HEADER_NAME]: `Token ${token}`
    };
    
    console.log('🔍 DEBUG: Final headers:', headers);

    return _callAPI(endpoint, method, body, headers, abortable);
}

function _callAPI(
    endpoint: string,
    method: MethodType = 'GET',
    body?: { [key: string]: any },
    headers?: { [key: string]: string },
    abortable: boolean = false
): Promise<any> {
    const config: {
        method: string;
        headers: { [key: string]: string };
        body?: string;
        signal?: AbortSignal;
    } = {
        method,
        headers: headers || {}
    };

    if (body) {
        config['headers']['Content-Type'] = 'application/json';
        config['body'] = JSON.stringify(body);
    }

    if (abortable) {
        config['signal'] = signal;
    }

    // Debug, make sure this path is correct 
    const joinURL = `${BASE_URL.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;
    
    // Use safe logging utility to mask sensitive data
    safeApiLog(joinURL, config); 

    return fetch(joinURL, config)
        .catch((err) => {
            // reject with unknown error, but aborting an abortable request should be
            // distinguishable as it's not necessarily an erroneous path
            // DEBUG
            console.error("❌ Fetch failed:", err); // ✅ LOG fetch error
            return Promise.reject({
                status: -1,
                aborted: abortable && err.name === 'AbortError'
            });
        })
        .then((response) =>
            response
                .json()
                .catch((_err) => {
                    // DEBUG
                    console.warn("⚠️ Could not parse response JSON");
                    return Promise.reject({ status: response.status });
                })
                .then((responseBody) => {
                    if (response.ok) {
                        return Promise.resolve(responseBody);
                    } else {
                        // error and description may not be available
                        console.error("❌ API error response:", response.status, responseBody);
                        return Promise.reject({
                            status: response.status,
                            data: responseBody,
                            error: responseBody.error,
                            description: responseBody.description
                        });
                    }
                })
        );
}
